import type { SisCredentialResponse, SisSchool } from '../../../../../../libs/common-interfaces';
import { Upload, Space, Spin } from 'antd';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import MainTemplate from '../Shell/MainTemplate';
import SchoolMapping from '../PSIntegrationSetup/SchoolMap';
import Col from '../../components-v2/Col';
import Divider from '../../components-v2/Divider';
import Row from '../../components-v2/Row';
import AlertErrorIcon from '../../../assets/Icon/AlertErrorIcon';
import { LoadingOutlined } from '@ant-design/icons';
import ConfigureEmail from '../ConfigureEmail';
import { IntegrationContext } from '../../utils/context';
import StatusInfo from './StatusInfo';

// Import extracted services
import { FileUploadStatus } from '../../utils/types';
import { uploadFile, validateFile, fetchFileUploadStatus } from '../../utils/fileUploadService';
import { fetchOneRosterSchools, saveSchoolMapping } from '../../utils/schoolService';
import {
  updateNotificationSettings,
  getNotificationSettings,
  updateTenantMeta,
  NotificationSettings,
} from '../../utils/notificationService';
import { createStatusPoller, checkConnectionStatus } from '../../utils/statusUtils';
import apiClient from '../../utils/apiClient';

const OneRosterSetup = (): React.ReactElement => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================

  // File Upload State
  const [files, setFiles] = React.useState<File | null>(null);
  const [fileJobId, setFileJobId] = React.useState('');
  const [oneRosterFilePath, setOneRosterFilePath] = React.useState('');
  const [fileSelectedName, setFileSelectedName] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isUploadInProgress, setIsUploadInProgress] = React.useState(false);
  const [uploadFileMessage, setUploadFileMessage] = React.useState('');
  const [fileUploadStatus, setFileUploadStatus] = React.useState<FileUploadStatus>({
    fileName: '',
    isUploading: false,
    uploadMessage: '',
    lastUpload: null,
  });

  // Connection & Status State
  const [isConnected, setIsConnected] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  // School Management State
  const [sisSchools, setSisSchools] = React.useState<SisSchool[]>([]);
  const [unusedSchools, setUnusedSchools] = React.useState<SisSchool[]>([]);
  const [unusedSisSchools, setUnusedSisSchools] = React.useState<SisSchool[]>([]);
  const [schoolMapping, setSchoolMapping] = React.useState([]);
  const [schoolMatchLoading, setSchoolMatchLoading] = React.useState(false);
  const [disableSave, setDisableSave] = React.useState(true);

  // Notification & Integration State
  const [staffEmails, setStaffEmails] = React.useState<string[]>([]);
  const [staffEmailsCount, setStaffEmailsCount] = React.useState<number>(0);
  const [notifications, setNotifications] = React.useState<NotificationSettings[]>([]);
  const [sisIntegration, setSisIntegration] = React.useState('psSis');
  const [isValidIntegration, setIsValidIntegration] = React.useState(true);
  const [clientId, setClientId] = React.useState('');

  // Context & Navigation
  const { isOneRosterIntegration, districtId } = React.useContext(IntegrationContext);
  const history = useHistory();
  const [credentials, setCredentials] = React.useState<SisCredentialResponse>({
    host: '',
    clientId: '',
    discoveryUrl: '',
    sisIntegration: '',
    dataHost: '',
  });

  // ========================================
  // EXTRACTED SERVICE HANDLERS
  // ========================================

  /**
   * Handles school mapping save using extracted service
   */
  const handleSaveSchoolSettings = async () => {
    try {
      setDisableSave(true);

      if (staffEmails.length > 0) {
        await handleSaveNotifications();
      }
      if (isConnected) {
        await saveSchoolMapping(schoolMapping, districtId, sisSchools);
      }

      goToConfigPage();
    } catch (error) {
      // Error handling is done in the service
      console.error('Failed to save school mapping:', error);
    }
  };

  /**
   * Handles fetching OneRoster schools using extracted service
   */
  const handleFetchOneRosterSchools = async () => {
    try {
      const schools = await fetchOneRosterSchools(oneRosterFilePath);
      setSisSchools(schools);
      setUnusedSchools(schools.filter((school) => school.type === 'school'));
    } catch (error) {
      // Error handling is done in the service
      console.error('Failed to fetch OneRoster schools:', error);
    }
  };

  /**
   * Handles file upload using extracted service
   */
  const handleFileUpload = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setErrorMessage('');
    setFileSelectedName(file.name);
    setFiles(file);

    try {
      await updateTenantMeta(isOneRosterIntegration, sisIntegration, clientId);

      const jobId = await uploadFile(
        file,
        () => {
          setUploadFileMessage('Uploading file...');
          setIsConnected(false);
          setIsUploadInProgress(true);
        },
        (jobId: string) => {
          setUploadFileMessage('File uploaded successfully...');
          setIsUploadInProgress(false);
          setIsConnected(true);
          setDisableSave(false);
          setFileJobId(jobId);
        },
        (error: string) => {
          setErrorMessage(error);
          setIsUploadInProgress(false);
        },
      );
    } catch (error) {
      console.error('File upload failed:', error);
      setErrorMessage('File upload failed');
    }
  };

  /**
   * Handles saving notification settings using extracted service
   */
  const handleSaveNotifications = async () => {
    try {
      const updatedNotifications = await updateNotificationSettings(notifications, staffEmails, isValidIntegration);
      setNotifications(updatedNotifications);
    } catch (error) {
      // Error handling is done in the service
      console.error('Failed to update notifications:', error);
    }
  };

  // ========================================
  // NAVIGATION & UTILITY FUNCTIONS
  // ========================================

  /**
   * Updates the school mapping state
   */
  const updateSchoolMapping = (newMapping) => {
    setSchoolMapping(newMapping);
    if (!loading) {
      setDisableSave(false);
    }
  };
  /**
   * Navigates to the configuration page
   */
  const goToConfigPage = async () => {
    history.push('/configure');
  };

  // ========================================
  // UI HELPER FUNCTIONS
  // ========================================

  /**
   * Renders the Cancel/Save button group
   */
  const cscBtns = (dataTestIdPrefix) => (
    <Space>
      <button className="button" onClick={goToConfigPage} data-test-id={`${dataTestIdPrefix}_cancel_btn`}>
        Cancel
      </button>
      <button
        className="buttonSolid"
        disabled={disableSave}
        onClick={handleSaveSchoolSettings}
        data-test-id={`${dataTestIdPrefix}_save_btn`}
      >
        Save
      </button>
    </Space>
  );

  /**
   * Handles file selection from the upload component
   */
  const selectedFile = async (file) => {
    setFileSelectedName('');
    await handleFileUpload(file.file);
  };

  // ========================================
  // REACT EFFECTS
  // ========================================

  /**
   * Effect: Start polling when file job ID is set
   */
  React.useEffect(() => {
    if (fileJobId) {
      console.log('Starting polling for file job ID:', fileJobId);
      const pollStatus = createStatusPoller(
        fileJobId,
        (status) => setFileUploadStatus((prev) => ({ ...prev, ...status })),
        (presignedUrl) => {
          setFiles(null);
          setOneRosterFilePath(presignedUrl);
        },
      );
      pollStatus();
    }
  }, [fileJobId]);

  /**
   * Effect: Update status display when file upload status changes
   */
  React.useEffect(() => {
    const connectionStatus = checkConnectionStatus(fileUploadStatus);
    setIsConnected(connectionStatus);
  }, [fileUploadStatus]);

  /**
   * Effect: Fetch initial data on component mount
   */
  React.useEffect(() => {
    const initializeComponent = async () => {
      try {
        const { data: meta } = await apiClient.get(`/data-ingest/sis/tenant-meta`);
        if (meta && meta.sisIntegration !== 'oneRoster' && meta.sisIntegration !== 'eSP') {
          setIsValidIntegration(false);
          setSisIntegration(meta.sisIntegration);
          return;
        }
        const statusData = await fetchFileUploadStatus();
        setFileUploadStatus((prev) => ({ ...prev, ...statusData }));

        const { notifications, tenantData, staffEmails } = await getNotificationSettings();
        setSisIntegration(tenantData.sisIntegration ?? 'oneRoster');
        setClientId(tenantData.clientId ?? '');
        setNotifications(notifications);
        setStaffEmails(staffEmails);
        setStaffEmailsCount(staffEmails.length);
        setIsValidIntegration(true);
      } catch (error) {
        console.error('Failed to initialize component:', error);
        setIsValidIntegration(false);
      }
    };

    initializeComponent();
  }, []);

  /**
   * Effect: Fetch schools when OneRoster file path is available
   */
  React.useEffect(() => {
    console.log('OneRoster file path:', oneRosterFilePath);
    // Only fetch schools if a file path is provided
    if (oneRosterFilePath !== '') {
      handleFetchOneRosterSchools();
    }
  }, [oneRosterFilePath]);

  /**
   * Effect: Enable Save button when staffEmails changed
   */
  React.useEffect(() => {
    if (staffEmails.length != staffEmailsCount) {
      setDisableSave(false);
    }
  }, [staffEmails]);

  // ========================================
  // RENDER
  // ========================================

  return (
    <>
      <MainTemplate
        title="OneRoster format settings"
        titleClassName="integrationSetupTitle"
        subheaderRSContent={cscBtns('upper')}
      >
        <div>
          <Row>
            <Col span={20}>
              <div>
                <h3>Upload file Status <StatusInfo isConnected={isConnected} /></h3>
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={20}>
              <div className="">
                <h3 className="uploadHeading">Note:</h3>
                <ul>
                  <li>
                    File naming convention must be: <b>NavianceOneRoster.zip</b>
                  </li>
                  <li>Only zip files are accepted. Max size of 1GB</li>
                </ul>
              </div>
              <div className="section" data-testid="one-roster-file-upload">
                <Col span="18">
                  <h2 className="uploadHeading">Data file:</h2>
                </Col>
              </div>
              <div className="subSection">
                <Row className="uploadSection">
                  <Col span="18">
                    <div className="fileUploadbtn" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {/* "Choose File" button */}
                      <Col style={{ display: 'inline-block' }} span="6">
                        <Upload showUploadList={false} accept=".zip" customRequest={selectedFile}>
                          <button className="button responsive-button" data-testid="file_upload_btn">
                            Choose File
                          </button>
                        </Upload>
                      </Col>

                      {/* Spinner and Upload Message */}
                      <Col style={{ display: 'inline-block' }} span="18">
                        <div>
                          {isUploadInProgress && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <Spin indicator={<LoadingOutlined spin />} size="large" />
                              <span>{uploadFileMessage}</span>
                            </div>
                          )}
                          {/* Error Message */}
                          <span style={errorMessage !== '' ? {} : { display: 'none' }}>
                            <AlertErrorIcon />
                            <span style={{ color: 'red' }}>{errorMessage}</span>
                          </span>
                        </div>
                      </Col>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="section">
                <Col span="18">
                  <Divider></Divider>
                </Col>
                <Row justify="start">
                  <Col span="18">
                    <h2 className="uploadHeading">File name</h2>
                  </Col>
                  <Col span={18}>
                    <div>
                      <input
                        className="fileNameInputBox drawerInputFull"
                        type="text"
                        defaultValue={fileSelectedName}
                        placeholder="File Name"
                        data-test-id="input_oneRosterFileName"
                        style={{ lineHeight: '36px !important' }}
                        disabled={true}
                      />
                    </div>
                  </Col>
                  <Col span={18}>
                    <Row>
                      <h2 className="uploadHeading">Email recipients</h2>
                    </Row>
                    <Row>
                      <span style={{ paddingTop: '10px' }} className="uploadcont">
                        Add recipients to receive email notifications about the import.
                      </span>
                    </Row>
                    <br />
                    <ConfigureEmail splitEmails={staffEmails} setSplitEmails={setStaffEmails} />
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </MainTemplate>
      {isConnected && (
        <MainTemplate title="">
          <SchoolMapping
            sisSchools={sisSchools.filter((school) => school.type !== 'district')}
            unusedSisSchools={unusedSchools}
            setUnusedSisSchools={setUnusedSchools}
            fetchSisSchools={() => handleFetchOneRosterSchools()}
            schoolMapping={schoolMapping}
            setSchoolMapping={updateSchoolMapping}
            schoolMatchLoading={loading}
            setSchoolMatchLoading={setLoading}
            credentials={credentials}
          />
        </MainTemplate>
      )}
    </>
  );
};

export default OneRosterSetup;
